import type { DscNoteResponse } from "@credit-acceptance/step6-bff-client";

/**
 * A single Pre-approval Change History row, normalized for the UI.
 *
 * The generated `DscNoteResponse` types every field as optional (`?: string`)
 * because the BFF forwards recap-service payloads verbatim. The table renders
 * verbatim values, so we keep the string fields but coerce the absent case to
 * an empty string at the mapping boundary (see {@link toChangeHistoryEntry}) —
 * that keeps the render path free of `?? ""` noise and matches caps2, which
 * shows an empty cell for new-add rows (no previous value).
 */
export interface DscChangeHistoryEntry {
  /** Raw date/time the change was recorded in recap (formatted for display at render). */
  date: string;
  /** Name of the attribute that changed (rendered verbatim). */
  attributeName: string;
  /** Previous value before the change; empty string for new-add rows. */
  oldValue: string;
  /** New value after the change. */
  newValue: string;
  /** Optional free-text note (not shown by caps2; carried through when present). */
  note?: string;
  /** Optional actor username (not shown by caps2; carried through when present). */
  changedByUser?: string;
}

/**
 * Normalizes a raw `DscNoteResponse` into a {@link DscChangeHistoryEntry},
 * coercing the string columns' absent case to `""` so the table renders a blank
 * cell rather than "undefined".
 * @param {DscNoteResponse} raw - A single change-history row from the BFF.
 * @returns {DscChangeHistoryEntry} The normalized entry.
 */
export function toChangeHistoryEntry(raw: DscNoteResponse): DscChangeHistoryEntry {
  return {
    date: raw.date ?? "",
    attributeName: raw.attributeName ?? "",
    oldValue: raw.oldValue ?? "",
    newValue: raw.newValue ?? "",
    note: raw.note,
    changedByUser: raw.changedByUser,
  };
}
